<template>
  <div>
    <form class="validate-form" @submit.prevent="handleSave">
      <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
        <UsersIcon />
        <h2 class="text-lg font-medium mr-auto">{{ $t('employee.add') }}</h2>
        <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
          <button
            type="submit"
            class="btn btn-primary shadow-md flex items-center"
          >
            <SaveIcon class="w-4 h-4 mr-2" /> {{ $t('common.save') }}
          </button>
          <router-link
            :to="{ name: 'side-menu-employees' }"
            tag="a"
            class="btn shadow-md flex items-center"
          >
            <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
          </router-link>
        </div>
      </div>
      <div class="pos intro-y grid grid-cols-12 gap-5 mt-5">
        <!-- BEGIN: Post Info -->
        <div class="col-span-12">
          <div class="intro-y box p-5">
            <div class="mt-3">
              <label
                for="name"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('common.personName') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }},
                  {{ $t('form.constraint.gte2ch') }}
                </span>
              </label>
              <input
                id="name"
                v-model.trim="validate.name.$model"
                type="text"
                name="name"
                class="
                  intro-y
                  form-control
                  py-3
                  px-4
                  box
                  pr-10
                  placeholder-theme-13
                "
                :class="{ 'border-theme-6': validate.name.$error }"
                placeholder="John"
              />
              <template v-if="validate.name.$error">
                <div
                  v-for="(error, index) in validate.name.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label
                for="lastname"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('common.personLastname') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }},
                  {{ $t('form.constraint.gte2ch') }},
                  {{ $t('common.personLastnameNotShowingOnWeb') }}
                </span>
              </label>
              <input
                id="lastname"
                v-model.trim="validate.lastname.$model"
                type="text"
                name="lastname"
                class="
                  intro-y
                  form-control
                  py-3
                  px-4
                  box
                  pr-10
                  placeholder-theme-13
                "
                :class="{ 'border-theme-6': validate.lastname.$error }"
                placeholder="Doe"
              />
              <template v-if="validate.lastname.$error">
                <div
                  v-for="(error, index) in validate.lastname.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label
                for="displayName"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('common.displayName') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.constraint.gte2ch') }}
                </span>
              </label>
              <input
                id="displayName"
                v-model.trim="validate.displayName.$model"
                type="text"
                name="displayName"
                class="
                  intro-y
                  form-control
                  py-3
                  px-4
                  box
                  pr-10
                  placeholder-theme-13
                "
                :class="{ 'border-theme-6': validate.displayName.$error }"
                placeholder="Johnny"
              />
              <template v-if="validate.displayName.$error">
                <div
                  v-for="(error, index) in validate.displayName.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <ImageUploader ref="imageUploader" />
            </div>
            <div class="mt-3">
              <label
                for="phoneNumber"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                {{ $t('common.phoneNumber') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.optional') }},
                  {{ $t('form.constraint.phoneFormat') }}
                </span>
              </label>
              <input
                id="phoneNumber"
                v-model.trim="validate.phoneNumber.$model"
                type="text"
                name="phoneNumber"
                class="
                  intro-y
                  form-control
                  py-3
                  px-4
                  box
                  pr-10
                  placeholder-theme-13
                "
                :class="{ 'border-theme-6': validate.phoneNumber.$error }"
                placeholder="+421xxxyyyzzz"
              />
              <template v-if="validate.phoneNumber.$error">
                <div
                  v-for="(error, index) in validate.phoneNumber.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label
                for="email"
                class="form-label w-full flex flex-col sm:flex-row"
              >
                Email
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.optional') }}
                </span>
              </label>
              <input
                id="email"
                v-model.trim="validate.email.$model"
                type="text"
                name="email"
                class="
                  intro-y
                  form-control
                  py-3
                  px-4
                  box
                  pr-10
                  placeholder-theme-13
                "
                :class="{ 'border-theme-6': validate.email.$error }"
                placeholder="info@info.info"
              />
              <template v-if="validate.email.$error">
                <div
                  v-for="(error, index) in validate.email.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <AddressForm v-model:address="address" />
            </div>
            <div class="mt-3">
              <ServiceMultiselect v-model:selectedServices="selectedServices" />
            </div>
            <div class="mt-3">
              <BranchMultiselect v-model:selectedBranches="selectedBranches" />
            </div>
            <div class="form-check flex-col items-start mt-3">
              <label for="visible" class="form-check-label ml-0 mb-2">
                {{ $t('common.visibility') }}
              </label>
              <input
                id="visible"
                v-model="validate.visible.$model"
                name="visible"
                class="form-check-switch"
                type="checkbox"
              />
            </div>
            <div class="form-check flex-col items-start mt-3">
              <label for="active" class="form-check-label ml-0 mb-2">
                {{ $t('common.active') }}
              </label>
              <input
                id="active"
                v-model="validate.active.$model"
                name="active"
                class="form-check-switch"
                type="checkbox"
              />
            </div>
          </div>
        </div>
        <!-- END: Post Info -->
      </div>
    </form>
  </div>
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('employee.created') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('employee.notCreated') }}!</div>
      <!-- <div class="text-gray-600 mt-1">{{ errorNote }}</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
</template>

<script>
import { defineComponent, ref, toRaw, toRefs, reactive } from 'vue'
import { required, minLength, helpers, email } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Toastify from 'toastify-js'
import AddressForm from '@/components/address/AddressForm.vue'
import ServiceMultiselect from '../../components/services/ServiceMultiselect.vue'
import BranchMultiselect from '../../components/branches/BranchMultiselect.vue'
import employeeApi from '@/services/employees/employee.api'
import ImageUploader from '@/components/cdn/ImageUploader.vue'

export default defineComponent({
  components: {
    ImageUploader,
    AddressForm,
    ServiceMultiselect,
    BranchMultiselect
  },
  methods: {
    checkEmpty(data) {
      if (data === null || data === undefined || data === '') {
        return null
      }
      return data
    },
    handleSave(data) {
      this.loading = true

      this.validate.$touch()
      if (this.validate.$invalid) {
        Toastify({
          node: cash('#failed-notification-content')
            .clone()
            .removeClass('hidden')[0],
          duration: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        this.loading = false
        return
      }

      const address = toRaw(this.address)
      const saveRequest = {
        name: data.target.elements.name.value,
        lastname: data.target.elements.lastname.value,
        displayName: this.checkEmpty(data.target.elements.displayName.value),
        image: this.$refs.imageUploader.getValue(),
        phoneNumber: data.target.elements.phoneNumber.value,
        mail: data.target.elements.email.value ?? null,
        address: address.street ? address : null,
        services: toRaw(this.selectedServices.selectedServices) ?? [],
        branches: toRaw(this.selectedBranches.selectedBranches) ?? [],
        visible: data.target.elements.visible.checked,
        active: data.target.elements.active.checked
      }

      employeeApi.createEmployee(saveRequest).then(
        response => {
          Toastify({
            node: cash('#success-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          console.log(response)
          this.$router.push({ name: 'side-menu-employees' })
        },
        error => {
          console.log(error)
          Toastify({
            node: cash('#failed-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.loading = false
        }
      )
    }
  },
  computed: {
    console: () => console
  },
  setup() {
    const selectedBranches = ref([])
    const selectedServices = ref([])
    const address = ref({})

    const phoneNumberValidator = helpers.regex(/^\+421[0-9]{9}$/)
    const formData = reactive({
      name: '',
      lastname: '',
      displayName: '',
      phoneNumber: '',
      email: '',
      visible: false,
      active: false
    })
    const rules = {
      name: {
        required: helpers.withMessage(
          'form.validation.personName.required',
          required
        ),
        minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
      },
      lastname: {
        required: helpers.withMessage(
          'form.validation.personLastname.required',
          required
        ),
        minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
      },
      displayName: {
        minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
      },
      phoneNumber: {
        phoneNumberValidator: helpers.withMessage(
          'form.validation.phoneNumberValidator',
          phoneNumberValidator
        )
      },
      email: {
        email: helpers.withMessage('form.validation.email', email)
      },
      visible: {},
      active: {}
    }
    const validate = useVuelidate(rules, toRefs(formData))

    return {
      address,
      validate,
      formData,
      selectedBranches,
      selectedServices
    }
  }
})
</script>
